import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, Shield, CheckCircle, Clock, Wrench, Settings, Truck } from 'lucide-react';

const PartsPage = () => {
  const partCategories = [
    {
      title: "Engine Components",
      description: "Complete range of MAN engine parts including pistons, valves, gaskets, and engine management components.",
      parts: ["Engine blocks and heads", "Pistons and connecting rods", "Turbochargers and intercoolers", "Engine management sensors", "Fuel injection components", "Cooling system parts"],
      icon: <Settings className="h-8 w-8 text-orange-600" />
    },
    {
      title: "Transmission & Drivetrain",
      description: "MAN transmission parts, clutch assemblies, differentials, and all drivetrain components for optimal performance.",
      parts: ["Gearbox assemblies", "Clutch plates and pressure plates", "Differential components", "Drive shafts and CV joints", "Transmission filters and fluids", "PTO units"],
      icon: <Wrench className="h-8 w-8 text-orange-600" />
    },
    {
      title: "Braking System",
      description: "Safety-critical braking components including air brake systems, discs, pads, and all brake hardware.",
      parts: ["Brake discs and drums", "Brake pads and shoes", "Air brake compressors", "Brake chambers and valves", "ABS components", "Handbrake assemblies"],
      icon: <Shield className="h-8 w-8 text-orange-600" />
    },
    {
      title: "Electrical Systems",
      description: "Modern MAN electrical components including ECUs, wiring harnesses, lighting systems, and diagnostic equipment.",
      parts: ["ECU modules and sensors", "Wiring harnesses", "Alternators and starters", "LED lighting systems", "Dashboard instruments", "MANCAT3 diagnostic tools"],
      icon: <CheckCircle className="h-8 w-8 text-orange-600" />
    },
    {
      title: "Cab & Body Parts",
      description: "Complete range of MAN cab components, body panels, mirrors, seats, and interior trim parts.",
      parts: ["Cab doors and windows", "Body panels and bumpers", "Mirrors and lighting", "Driver seats and trim", "Dashboard components", "Air conditioning parts"],
      icon: <Truck className="h-8 w-8 text-orange-600" />
    },
    {
      title: "Suspension & Steering",
      description: "MAN suspension components, steering systems, and chassis parts for smooth operation and handling.",
      parts: ["Leaf springs and air bags", "Shock absorbers", "Steering boxes and pumps", "Tie rods and ball joints", "King pins and bushes", "Axle components"],
      icon: <Settings className="h-8 w-8 text-orange-600" />
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative bg-black text-white py-20">
        <div className="absolute inset-0 bg-gradient-to-r from-black/90 to-black/70"></div>
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1558618047-3c8c76ca7d13?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHw1fHx0cnVjayUyMHBhcnRzfGVufDB8fHx8MTc1NjExMjU1Mnww&ixlib=rb-4.1.0&q=85')`
          }}
        ></div>
        
        {/* Enhanced black overlay for better text readability */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 to-black/80"></div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6 font-inter">
            MAN Truck Parts & Supply
          </h1>
          <p className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto font-inter">
            Comprehensive parts supply for MAN trucks. Genuine manufacturer parts, OEM quality replacements, and extensive used parts inventory from our 30+ years of MAN expertise.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/parts-catalogue" className="bg-orange-600 hover:bg-orange-700 text-white px-8 py-3 rounded font-bold transition-colors shadow-lg inline-block">
              Browse Parts Catalogue
            </Link>
            <button className="border-2 border-white text-white hover:bg-white hover:text-black px-8 py-3 rounded font-bold transition-colors shadow-lg">
              Request Custom Quote
            </button>
          </div>
        </div>
      </section>

      {/* Parts Categories */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-black mb-4 font-inter">
              Comprehensive MAN Parts Supply
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto font-inter">
              From routine maintenance parts to major component replacements, we maintain extensive inventory of genuine MAN parts and quality used components through our comprehensive supply and sourcing network.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {partCategories.map((category, index) => (
              <div key={index} className="bg-white border border-gray-200 rounded-2xl shadow-lg p-6 hover:shadow-xl transition-shadow">
                <div className="flex items-center mb-4">
                  {category.icon}
                  <h3 className="text-xl font-bold text-black ml-3 font-inter">{category.title}</h3>
                </div>
                
                <p className="text-gray-600 mb-4 font-inter">
                  {category.description}
                </p>
                
                <ul className="space-y-2 mb-4">
                  {category.parts.map((part, partIndex) => (
                    <li key={partIndex} className="flex items-center text-sm text-gray-700">
                      <CheckCircle className="h-4 w-4 text-green-600 mr-2 flex-shrink-0" />
                      {part}
                    </li>
                  ))}
                </ul>
                
                <button className="w-full bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded font-medium transition-colors">
                  Contact Us
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Used Parts Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left Column */}
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-black mb-6 font-inter">
                Quality Used MAN Parts
              </h2>
              <p className="text-lg text-gray-600 mb-6 font-inter">
                Extensive inventory of quality used MAN truck parts sourced through our professional supply and sourcing network. All used parts are carefully inspected and tested to ensure reliability and performance.
              </p>
              
              <div className="space-y-4 mb-8">
                <div className="flex items-start">
                  <CheckCircle className="h-6 w-6 text-green-600 mt-1 flex-shrink-0" />
                  <div className="ml-3">
                    <h4 className="font-bold text-black">Complete Engine Assemblies</h4>
                    <p className="text-gray-600">Tested MAN engines with compression and performance reports</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <CheckCircle className="h-6 w-6 text-green-600 mt-1 flex-shrink-0" />
                  <div className="ml-3">
                    <h4 className="font-bold text-black">Gearbox & Transmission Parts</h4>
                    <p className="text-gray-600">Manual and automatic transmissions, clutch assemblies, and drivetrain components</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <CheckCircle className="h-6 w-6 text-green-600 mt-1 flex-shrink-0" />
                  <div className="ml-3">
                    <h4 className="font-bold text-black">Axles & Suspension Components</h4>
                    <p className="text-gray-600">Front and rear axles, differential assemblies, and suspension parts</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <CheckCircle className="h-6 w-6 text-green-600 mt-1 flex-shrink-0" />
                  <div className="ml-3">
                    <h4 className="font-bold text-black">Body Panels & Cab Parts</h4>
                    <p className="text-gray-600">Doors, bonnets, wings, mirrors, and complete cab assemblies</p>
                  </div>
                </div>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <button className="bg-orange-600 hover:bg-orange-700 text-white px-6 py-3 rounded font-bold transition-colors">
                  Browse Used Parts
                </button>
                <button className="border-2 border-orange-600 text-orange-600 hover:bg-orange-600 hover:text-white px-6 py-3 rounded font-bold transition-colors">
                  Check Availability
                </button>
              </div>
            </div>
            
            {/* Right Column - Stats */}
            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <h3 className="text-2xl font-bold text-black mb-6 font-inter">Parts Service Benefits</h3>
              
              <div className="space-y-6">
                <div className="flex items-center">
                  <div className="bg-orange-100 rounded-full w-12 h-12 flex items-center justify-center mr-4">
                    <Shield className="h-6 w-6 text-orange-600" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-black">12 Month</div>
                    <div className="text-sm text-gray-600">Warranty on Used Parts</div>
                  </div>
                </div>
                
                <div className="flex items-center">
                  <div className="bg-orange-100 rounded-full w-12 h-12 flex items-center justify-center mr-4">
                    <Clock className="h-6 w-6 text-orange-600" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-black">Next Day</div>
                    <div className="text-sm text-gray-600">Delivery Available</div>
                  </div>
                </div>
                
                <div className="flex items-center">
                  <div className="bg-orange-100 rounded-full w-12 h-12 flex items-center justify-center mr-4">
                    <Wrench className="h-6 w-6 text-orange-600" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-black">30+ Years</div>
                    <div className="text-sm text-gray-600">MAN Parts Experience</div>
                  </div>
                </div>
                
                <div className="flex items-center">
                  <div className="bg-orange-100 rounded-full w-12 h-12 flex items-center justify-center mr-4">
                    <CheckCircle className="h-6 w-6 text-orange-600" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-black">Quality</div>
                    <div className="text-sm text-gray-600">Tested & Guaranteed</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-16 bg-black text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-6 font-inter">
            Need MAN Parts? We're Here to Help
          </h2>
          <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
            Contact our parts specialists for genuine MAN parts, OEM replacements, or quality used components.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <div className="text-center">
              <Phone className="h-12 w-12 text-orange-500 mx-auto mb-4" />
              <h3 className="text-lg font-bold mb-2">Call Our Parts Team</h3>
              <p className="text-orange-500 font-bold">01530 231 235</p>
            </div>
            
            <div className="text-center">
              <Mail className="h-12 w-12 text-orange-500 mx-auto mb-4" />
              <h3 className="text-lg font-bold mb-2">Email Your Requirements</h3>
              <p className="text-orange-500 font-bold">sales@lawmancommercials.com</p>
            </div>
            
            <div className="text-center">
              <Clock className="h-12 w-12 text-orange-500 mx-auto mb-4" />
              <h3 className="text-lg font-bold mb-2">Opening Hours</h3>
              <p className="text-gray-300">Mon-Fri: 8AM-6PM<br />Sat: 8AM-4PM</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PartsPage;